import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useJuice } from '@/juice/useJuice'
import { MapSheet } from '@/features/map/MapSheet'
import { useBuddies } from '@/store/buddies'
import { useGifts } from '@/store/gifts'
import { useChurchCheckIns } from '@/store/churchCheckIns'
import { useChurchReplies } from '@/store/churchReplies'
import { useReviews } from '@/store/reviews'
import { useSettings } from '@/store/settings'
import { useAccountLocked } from '@/components/AccountWall'
import { Icon, type IconId } from '@/data/icons'

// Five tabs, one per thing you actually come here to do. Ranks folded into
// Play; Buddies and Cards folded into You — each still a full screen at its own
// URL, just no longer competing for a slot down here.
//
// `guest: false` means the tab shows an account wall to a guest (App.tsx's WALL
// table is the authority; this flag only decides whether the little padlock is
// drawn). The tabs stay VISIBLE and tappable for guests on purpose — a locked
// tab you can look into is the pitch, and hiding half the nav would make the
// app look smaller than it is.
//
// The icons are PAINTED (`public/nav/*.png`), supplied by the app's owner on
// 2026-10-21 and drawn at 40px — a deliberate reversal of the drawn-paths nav
// (see CLAUDE.md, "Icons"). A baked image can't take a runtime colour, so the
// three states a tab has are carried by TONE instead of ink: full colour on
// the active tab (on the `--select` pill), muted and desaturated when not, and
// greyer still behind a padlock. `icon` stays as the drawn fallback for an
// image that fails to load, and is what the map's area headings still use.
const tabs: { to: string; label: string; icon: IconId; art: string; guest: boolean }[] = [
  { to: '/play', label: 'Play', icon: 'play', art: '/nav/play.png', guest: true },
  { to: '/battle', label: 'Battle', icon: 'battle', art: '/nav/battle.png', guest: false },
  { to: '/study', label: 'Study', icon: 'study', art: '/nav/study.png', guest: false },
  { to: '/church', label: 'Church', icon: 'church', art: '/nav/church.png', guest: false },
  { to: '/you', label: 'You', icon: 'you', art: '/nav/you.png', guest: true },
]

// Every PNG in public/nav is a 132px SQUARE with its painting scaled for equal
// visual weight and placed at its optical centre (solid shapes like the play
// triangle smaller, the thin swords larger) — so the bar draws them all in the
// same box with no per-icon CSS. Re-cut a painting and that balancing has to
// be redone on the file, not patched here.
const NAV_ICON = 40
// Every tab is the same width, so each icon's centre lands at the same place
// in its slot whatever the label under it says.
const TAB_W = 46

// A painted tab icon, falling back to the drawn path if the PNG never loads —
// a nav with a hole in it is worse than a nav in the old style.
function NavArt({ src, icon, size, tone }: { src: string; icon: IconId; size: number; tone: 'on' | 'off' | 'locked' }) {
  const [broken, setBroken] = useState(false)
  if (broken) return <Icon id={icon} size={size * 0.55} />
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      width={size}
      height={size}
      draggable={false}
      onError={() => setBroken(true)}
      style={{
        width: size,
        height: size,
        objectFit: 'contain',
        display: 'block',
        // Muted, not bleached: a heavy desaturate washed the gold play
        // triangle out to beige while the darker icons barely changed.
        // Locked stays readable as a picture (0.42 lost the painting entirely on
        // the dark bar); the padlock and the grey say "locked", not the fade.
        opacity: tone === 'on' ? 1 : tone === 'off' ? 0.72 : 0.58,
        filter: tone === 'on' ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.45))' : tone === 'off' ? 'saturate(0.8) brightness(0.92)' : 'grayscale(0.7)',
        transition: 'opacity 0.2s, filter 0.2s',
      }}
    />
  )
}

// A pending buddy request lives two taps deep on the You tab, so nothing out
// here said it existed. One dot — no count, no red, no badge that demands
// clearing. It marks that someone is waiting on you, which is the one thing
// in this app another person can be blocked by.
//
// The Study tab wears the same dot when verses are due for review. That used to
// be a whole card on the Play tab pointing down here, which is a signpost to a
// tab that is already on screen — the dot says it in no space at all. It is
// deliberately the SAME dot: no count, because "15 verses overdue" is a backlog
// to feel behind on, and this app doesn't do those. It is an invitation.
function NavDot() {
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        top: -1,
        right: -1,
        width: 9,
        height: 9,
        borderRadius: 999,
        background: 'var(--gold)',
        border: '2px solid rgba(30, 21, 28,0.95)',
        boxSizing: 'content-box',
      }}
    />
  )
}

// A padlock, same corner as the buddy dot. Quiet on purpose: it marks what an
// account opens, it doesn't scold anyone for not having one.
function NavLock() {
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        lineHeight: 0,
        opacity: 0.8,
      }}
    >
      <Icon id="lock" size={10} />
    </span>
  )
}

// The compass has been opened at least once on this device.
//
// Device-local in both modes, and deliberately so — the same break with the
// two-mode invariant `store/looks.ts` and `store/music.ts` make. Whether this
// phone has been shown where the map is is a fact about the phone, not a
// possession: it grants nothing, and syncing it would mean a table and an RPC
// to remember that somebody once tapped a button.
const MAP_SEEN_KEY = 'va.map.seen'

function readMapSeen(): boolean {
  try {
    return localStorage.getItem(MAP_SEEN_KEY) === '1'
  } catch {
    // Private mode / storage full: treat it as seen rather than pulsing at
    // somebody forever. A hint that can never be dismissed is a nag.
    return true
  }
}

function markMapSeen() {
  try {
    localStorage.setItem(MAP_SEEN_KEY, '1')
  } catch {
    /* ignore */
  }
}

// The compass — every place in this app, one tap from wherever you are.
//
// It sits BESIDE the nav pill rather than inside it, and both halves of that
// are deliberate. Beside, because five tabs already have to clear a 320px
// phone and a sixth would shrink every one of them. In the nav's own row,
// because that band is the only strip of the screen the app shell already
// reserves (96px of bottom padding) — a free-floating button anywhere else
// lands on top of page content, and every screen here anchors its primary
// action to the bottom. A control that covers the button somebody is reaching
// for is the exact trap `StudyDropToast` moved to the top of the screen to
// avoid.
//
// Round and gold-ringed so it reads as a different KIND of thing from the five
// tabs: they are places, this is a directory of them. It is never a tab and
// never shows as "active", because you are never on it.
function CompassPuck({ onOpen, hint }: { onOpen: () => void; hint: boolean }) {
  // The pulse is motion that never stops, which is exactly the kind this
  // setting exists to turn off. The gold RING stays either way — that is the
  // part carrying the meaning, and a hint that only exists as movement is a
  // hint reduce-motion players never get.
  const reduceMotion = useSettings((s) => s.reduceMotion)
  const pulsing = hint && !reduceMotion
  return (
    <motion.button
      onClick={onOpen}
      aria-label="Find your way around"
      whileTap={{ scale: 0.88 }}
      animate={pulsing ? { scale: [1, 1.06, 1] } : { scale: 1 }}
      transition={pulsing ? { duration: 2.4, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}
      style={{
        pointerEvents: 'auto',
        flexShrink: 0,
        width: 50,
        height: 50,
        borderRadius: 999,
        display: 'grid',
        placeItems: 'center',
        fontSize: 21,
        lineHeight: 1,
        background: 'rgba(30, 21, 28,0.85)',
        // The one visual difference that carries the meaning: a gold ring on
        // the first run, so the thing that explains the app is itself findable.
        // It stops the moment it is opened once — see MAP_SEEN_KEY above.
        border: `1px solid ${hint ? 'var(--gold)' : 'var(--stroke)'}`,
        boxShadow: hint
          ? '0 10px 30px rgba(0,0,0,0.5), 0 0 0 3px rgba(255,210,63,0.18)'
          : '0 10px 30px rgba(0,0,0,0.5)',
        backdropFilter: 'blur(14px)',
        color: hint ? 'var(--gold)' : 'var(--ink-dim)',
      }}
    >
      <NavArt src="/nav/compass.png" icon="compass" size={NAV_ICON} tone={hint ? 'on' : 'off'} />
    </motion.button>
  )
}

// Native-feeling tab bar pinned above the home indicator. Springy icon pop on
// the active tab. Tapping fires a light select sound/haptic.
export function BottomNav() {
  const juice = useJuice()
  const buddyRequests = useBuddies((s) => s.requests.length)
  // Anything addressed to you that you have not seen. Still ONE dot with no
  // count — a gift and a buddy request are both "there's something for you",
  // and a number here would turn a letterbox into a queue to be cleared.
  const unseenGifts = useGifts((s) => s.unseen) + useChurchCheckIns((s) => s.unseen) + useChurchReplies((s) => s.unseen)
  // Read-only, like the two above: HomeScreen calls loadDue() and everybody
  // lands there, so the schedule is loaded by the time this matters. The nav
  // never fetches — a tab bar mounted on every screen must not be a network
  // call on every screen.
  const reviewsDue = useReviews((s) => s.dueRefs.length)
  const locked = useAccountLocked()
  const reduceMotion = useSettings((s) => s.reduceMotion)
  const [mapOpen, setMapOpen] = useState(false)
  // The pulse is read ONCE, at mount, and cleared the first time the compass is
  // opened. Re-reading it per render would be a localStorage hit on every tab
  // change, and the same freeze-at-mount habit the arcade invite uses for its
  // have-they-played decision.
  const [mapSeen, setMapSeen] = useState(readMapSeen)

  const openMap = () => {
    juice.select()
    if (!mapSeen) { markMapSeen(); setMapSeen(true) }
    setMapOpen(true)
  }

  return (
    <>
    <nav
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 40,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      {/* Three columns: an empty one, the pill, the compass. The two outer
          columns share what is left equally, so the five tabs sit on the
          viewport's true centre and the compass hangs off the pill's right
          edge rather than dragging the whole row 29px left (which is what
          centring pill + compass as one unit did). Where the phone is too
          narrow for a 58px column on BOTH sides (a 320px phone has ~30), the
          grid gives the compass its room from the empty side, so the pill
          drifts left only by what it must and never overflows. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          width: 'calc(100vw - 12px)',
          marginBottom: 'calc(var(--safe-bottom) + 10px)',
        }}
      >
      <span aria-hidden />
      <div
        style={{
          pointerEvents: 'auto',
          display: 'flex',
          // Five tabs have to clear a 320px-wide phone, so the gaps and the pill
          // padding below are as tight as they can be without cramping the taps.
          gap: 1,
          minWidth: 0,
          padding: 6,
          borderRadius: 999,
          background: 'rgba(30, 21, 28,0.85)',
          // The same grain and lamplight as a card (index.css), and a stronger
          // catch-light on the top rim: the pill floats over everything, so
          // it is the surface that most needs to read as having a front face.
          backgroundImage: 'var(--lamp), var(--tex-grain)',
          border: '1px solid var(--stroke)',
          backdropFilter: 'blur(14px)',
          boxShadow: 'inset 0 1px 0 rgba(255, 236, 214, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.32), 0 10px 30px rgba(0,0,0,0.5)',
        }}
      >
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            onClick={() => juice.select()}
            style={{ textDecoration: 'none' }}
          >
            {({ isActive }) => (
              <motion.div
                // Every tab at the same size: shrinking the inactive four made
                // the selected one read as enlarged, and the bar lopsided
                // whenever it was an end tab. The bronze bubble and the tone
                // already say which one is on.
                animate={{ scale: 1 }}
                whileTap={reduceMotion ? undefined : { scale: 0.85 }}
                transition={{ type: 'spring', stiffness: 600, damping: 18 }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  width: TAB_W,
                  padding: '5px 0 6px',
                  borderRadius: 999,
                  position: 'relative',
                  // Warm bronze rather than the app's violet `--select`: the
                  // painted icons are all earth and gold, and a violet pill
                  // behind them was the one cold thing in the bar.
                  background: isActive ? 'linear-gradient(180deg, rgba(154,124,69,0.55), rgba(154,124,69,0.22))' : 'transparent',
                  color: isActive ? 'var(--ink)' : 'var(--ink-faint)',
                  boxShadow: isActive ? 'inset 0 0 0 1px var(--edge), 0 4px 14px rgba(0,0,0,0.4)' : 'none',
                }}
              >
                {/* The dot and the padlock anchor to the ICON's box, not the
                    tab's, so they sit on the same corner of every picture. */}
                <span style={{ position: 'relative', width: NAV_ICON, height: NAV_ICON, display: 'grid', placeItems: 'center' }}>
                  <NavArt src={t.art} icon={t.icon} size={NAV_ICON} tone={isActive ? 'on' : locked && !t.guest ? 'locked' : 'off'} />
                {t.to === '/you' && buddyRequests + unseenGifts > 0 && <NavDot />}
                {/* Not while the tab is locked: the padlock sits in the same
                    corner, and a guest can't reach the reviews anyway. */}
                {t.to === '/study' && !locked && reviewsDue > 0 && <NavDot />}
                {locked && !t.guest && <NavLock />}
                </span>
                <span style={{ fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap' }}>{t.label}</span>
              </motion.div>
            )}
          </NavLink>
        ))}
      </div>
      <div style={{ justifySelf: 'start', display: 'flex', marginLeft: 8 }}>
        <CompassPuck onOpen={openMap} hint={!mapSeen} />
      </div>
      </div>
    </nav>

    {/* Sibling of <nav>, never a child of it. The nav sets z-index 40, which
        creates a stacking context — a sheet nested inside it would paint at 40
        no matter what its own z-index said, and would end up UNDER the player
        card and every other sheet in the app. */}
    <AnimatePresence>
      {mapOpen && <MapSheet key="map" onClose={() => setMapOpen(false)} />}
    </AnimatePresence>
    </>
  )
}
